import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { type SidebarGroup, type SidebarItem, getSidebarGroupsForRole } from './sidebarConfig';
import { UserRole } from '../../types/UserRole';

interface SidebarBaseProps {
  isOpen: boolean;
  onCloseMobile?: () => void;
  role: UserRole;
}

const navItemBase =
  'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400/30';
const navItemInactive =
  'text-slate-700/80 hover:text-slate-900 hover:bg-slate-100';
const navItemActive =
  'bg-blue-600/12 text-blue-700 shadow-none ring-1 ring-blue-500/25 hover:text-blue-800 hover:bg-blue-600/16';
const navIconBase =
  'flex h-[18px] w-[18px] shrink-0 items-center justify-center stroke-[1.7]';
const navIconActive = 'text-blue-600';
const navIconInactive =
  'text-slate-500/90 group-hover:text-slate-700 transition-colors';
const navGroupLabel =
  'mb-2.5 px-3 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500';
const navItemActiveBar =
  'absolute left-0 top-1/2 h-5 -translate-y-1/2 w-0.5 rounded-r-full bg-blue-600 shadow-[0_0_8px_0_rgba(37,99,235,0.35)]';

function renderNavItem(item: SidebarItem, onCloseMobile?: () => void) {
  const Icon = item.icon;
  return (
    <li key={item.path}>
      <NavLink
        to={item.path}
        onClick={onCloseMobile}
        end={item.end}
        className={({ isActive }) =>
          `${navItemBase} ${isActive ? navItemActive : navItemInactive}`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && <span className={navItemActiveBar} aria-hidden="true" />}
            <Icon
              className={`${navIconBase} ${
                isActive ? navIconActive : navIconInactive
              }`}
            />
            <span className="flex-1 truncate">{item.label}</span>
          </>
        )}
      </NavLink>
    </li>
  );
}

export default function SidebarBase({ isOpen, onCloseMobile, role }: SidebarBaseProps) {
  const { user } = useAuth();
  const groups = getSidebarGroupsForRole(role);

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 w-64 transform border-r border-slate-300/70 bg-white shadow-[8px_0_48px_-12px_rgba(15,23,42,0.22),4px_0_30px_-8px_rgba(30,64,175,0.08)] transition-transform duration-300 ease-out lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      aria-label="Navegação principal"
    >
      <div className="relative flex h-full flex-col">
        <div className="relative shrink-0 min-h-[240px] border-b border-slate-200 bg-white px-4 pt-5 pb-5">
          <div className="flex flex-col items-center justify-center w-full">
            <div className="relative flex items-center justify-center shrink-0 w-[90%] max-w-[228px] h-[220px] max-h-[220px] min-h-[220px]">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-blue-500/16 via-blue-600/10 to-transparent blur-2xl" aria-hidden="true" />
              <img
                src="/suafrota_dasilva.png"
                alt="SuaFrota"
                loading="eager"
                decoding="sync"
                className="relative w-full h-full object-contain drop-shadow-[0_12px_34px_rgba(37,99,235,0.16)] select-none"
                draggable={false}
              />
            </div>
          </div>
        </div>

        <nav className="relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 py-4 scroll-smooth scrollbar-thin">
          {groups.map((group: SidebarGroup) => {
            if (group.visibleForRole && user && !group.visibleForRole.includes(user.role as UserRole)) {
              return null;
            }
            return (
              <div key={group.label} className="mb-6 last:mb-0">
                <div className={navGroupLabel}>{group.label}</div>
                <ul className="space-y-1">
                  {group.items.map((item) => renderNavItem(item, onCloseMobile))}
                </ul>
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
